import axios, { AxiosInstance } from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api/v1';

class ApiService {
  private api: AxiosInstance;
  private adminToken: string | null = null;

  constructor() {
    this.api = axios.create({
      baseURL: API_BASE_URL,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Add interceptor to automatically add admin token to requests
    this.api.interceptors.request.use((config) => {
      if (this.adminToken) {
        config.headers.Authorization = `Bearer ${this.adminToken}`;
      }
      return config;
    });

    // Load saved admin token
    const savedToken = localStorage.getItem('admin_token');
    if (savedToken) {
      this.adminToken = savedToken;
    }
  }

  // Admin Authentication
  async adminLogin(username: string, password: string) {
    try {
      const response = await this.api.post('/auth/login', {
        username,
        password,
      });

      // Check if user has admin role
      if (response.data.user?.role !== 'admin') {
        throw new Error('Access denied: Admin role required');
      }

      this.adminToken = response.data.token;
      if (this.adminToken) {
        localStorage.setItem('admin_token', this.adminToken);
        localStorage.setItem('admin_user', JSON.stringify(response.data.user));
      }

      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || error.message || 'Admin login failed');
    }
  }

  async adminLogout() {
    // Revoke the token server-side (best-effort) before clearing local state.
    try {
      if (this.adminToken) {
        await this.api.post('/auth/logout');
      }
    } catch {
      // ignore — local cleanup below still runs
    }
    this.adminToken = null;
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_user');
  }

  isAdminAuthenticated(): boolean {
    return !!this.adminToken;
  }

  getAdminUser(): any | null {
    const adminUser = localStorage.getItem('admin_user');
    return adminUser ? JSON.parse(adminUser) : null;
  }

  // Admin - Portal Management
  async createPortal(data: {
    name: string;
    contact_email: string;
    rate_limit_per_minute?: number;
    rate_limit_per_day?: number;
    cost_per_search?: number;
  }) {
    const response = await this.api.post('/admin/portals', data);
    return response.data;
  }

  async listPortals(params?: { status?: string; limit?: number; offset?: number }) {
    const response = await this.api.get('/admin/portals', { params });
    return response.data;
  }

  async getPortal(id: string) {
    const response = await this.api.get(`/admin/portals/${id}`);
    return response.data;
  }

  async updatePortal(id: string, data: {
    name?: string;
    contact_email?: string;
    status?: string;
    rate_limit_per_minute?: number;
    rate_limit_per_day?: number;
    cost_per_search?: number;
  }) {
    const response = await this.api.patch(`/admin/portals/${id}`, data);
    return response.data;
  }

  async deletePortal(id: string) {
    const response = await this.api.delete(`/admin/portals/${id}`);
    return response.data;
  }

  async regenerateCredentials(id: string) {
    const response = await this.api.post(`/admin/portals/${id}/regenerate-credentials`);
    return response.data;
  }

  // Admin Statistics
  async getAdminStatistics(params?: {
    portal_id?: string;
    start_date?: string;
    end_date?: string;
  }) {
    const response = await this.api.get('/admin/statistics', { params });
    return response.data;
  }

  // Search log — individual API calls with dual-cost and margin
  async getCalls(params?: {
    page?: number;
    page_size?: number;
    portal_id?: string;
    user_reference?: string;
    endpoint?: string;
    cache_hit?: 'true' | 'false';
    from?: string;
    to?: string;
  }) {
    const response = await this.api.get('/admin/calls', { params });
    return response.data;
  }

  // Statistics
  async getDashboardStats(period: string = '7d') {
    const response = await this.api.get('/stats/dashboard', {
      params: { period },
    });
    return response.data;
  }

  async getUserStats(period: string = '7d', userReference?: string) {
    const response = await this.api.get('/stats/users', {
      params: { period, user_reference: userReference },
    });
    return response.data;
  }

  async getEndpointStats(period: string = '7d') {
    const response = await this.api.get('/stats/endpoints', {
      params: { period },
    });
    return response.data;
  }

  async exportStats(period: string = '30d', type: 'calls' | 'daily' = 'calls') {
    const response = await this.api.get('/stats/export', {
      params: { period, type },
      responseType: 'blob',
    });

    // Create download link
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `stats_${type}_${Date.now()}.csv`);
    document.body.appendChild(link);
    link.click();
    link.remove();

    return true;
  }

  // System Events
  async getSystemEvents(params?: {
    severity?: string;
    event_type?: string;
    limit?: number;
    offset?: number;
  }) {
    const response = await this.api.get('/admin/events', { params });
    return response.data;
  }
}

export default new ApiService();
